import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Download, FileDown, MessageCircle } from "lucide-react";
import { INR } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Pixel-perfect share card for the affiliate. ASMs snap it to a PNG
 * or PDF and share on WhatsApp/DM. Deliberately uses fixed pixel
 * dimensions and inline SVGs to render identically in html2canvas.
 *
 * Layout: 640 x auto card, 40px padding, single column.
 */
export function AffiliateSnapshot({ open, onOpenChange, affiliate, nextSteps, monthKey }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!affiliate || !nextSteps) return null;

  const cm = nextSteps.current_month;
  const cw = nextSteps.current_window;
  const comm = nextSteps.commission_this_month;
  const nextTier = nextSteps.steps.find((s) => !s.done && s.target !== "Sustain Legend");
  const ft = nextSteps.fast_track;

  // Derive the label for the selected month; fall back to the snapshot's own month, then today's month.
  const derivedMonthKey = monthKey || cm?.month || "";
  const monthLabel = (() => {
    if (derivedMonthKey && /^\d{4}-\d{2}$/.test(derivedMonthKey)) {
      const [y, m] = derivedMonthKey.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    }
    return new Date().toLocaleString("en-IN", { month: "long", year: "numeric" });
  })();

  const runExport = async (kind) => {
    if (!cardRef.current) return null;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: 720,
      });
      if (kind === "png") {
        const url = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = url;
        a.download = `${affiliate.name.replace(/\s+/g, "_")}_snapshot_${derivedMonthKey || "current"}.png`;
        a.click();
        toast.success(`Snapshot downloaded — ${monthLabel}`);
      } else if (kind === "pdf") {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgW = pageWidth - 20;
        const imgH = (canvas.height * imgW) / canvas.width;
        const y = Math.max(10, (pageHeight - imgH) / 2);
        pdf.addImage(imgData, "PNG", 10, y, imgW, imgH);
        pdf.save(`${affiliate.name.replace(/\s+/g, "_")}_snapshot_${derivedMonthKey || "current"}.pdf`);
        toast.success(`Snapshot PDF downloaded — ${monthLabel}`);
      }
      setBusy(false);
      return canvas;
    } catch (e) {
      toast.error("Could not export");
      setBusy(false);
      return null;
    }
  };

  const shareOnWhatsApp = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    // 1) Render the card to canvas
    let canvas;
    try {
      canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: 720,
      });
    } catch (e) {
      toast.error("Could not render snapshot");
      setBusy(false);
      return;
    }

    // 2) Trigger a PNG download (so it's ready to attach if paste fails)
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${affiliate.name.replace(/\s+/g, "_")}_snapshot_${derivedMonthKey || "current"}.png`;
    a.click();

    // 3) Try to also copy the image to the clipboard (works on Chrome/Edge desktop
    //    on secure origins). This lets the ASM just paste (Ctrl+V) inside WhatsApp Web.
    let clipboardCopied = false;
    try {
      if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        const blob = await (await fetch(dataUrl)).blob();
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        clipboardCopied = true;
      }
    } catch (e) {
      clipboardCopied = false;
    }

    // 4) Build a friendly personalized text with the key stats
    const nextTierLine = nextTier
      ? nextTier.target === "Pro"
        ? `\n🎯 Next tier: *${nextTier.target}* — ${Math.max(0, 4 - (nextSteps.lifetime_new_customers || 0))} more new customer${Math.max(0, 4 - (nextSteps.lifetime_new_customers || 0)) === 1 ? "" : "s"} to unlock.`
        : `\n🎯 Next tier: *${nextTier.target}* — need ${Math.max(0, newTarget - cw.new_customers)} more new customer${Math.max(0, newTarget - cw.new_customers) === 1 ? "" : "s"} and ₹${Math.max(0, revTarget - cw.revenue).toLocaleString("en-IN")} more revenue (in trailing 3-month window).${nextTier.unlock_reward_mrp ? ` Unlock bonus: *₹${nextTier.unlock_reward_mrp.toLocaleString("en-IN")} MRP*.` : ""}`
      : "";
    const commLine = `💰 ${monthLabel}: ${comm.new_customers_this_month || 0} new customer${(comm.new_customers_this_month || 0) === 1 ? "" : "s"} · ${comm.current_percent}% commission.`;
    const rewardLine = (cm.reward_slab_unlocked_mrp || 0) > 0
      ? `\n🎁 Free products unlocked in ${monthLabel}: *₹${(cm.reward_slab_unlocked_mrp || 0).toLocaleString("en-IN")} MRP*.`
      : "";
    const message =
      `Hi ${affiliate.name.split(" ")[0]}, here's your Fuelled performance snapshot for ${monthLabel} 👇\n\n` +
      `${commLine}${rewardLine}${nextTierLine}\n\n` +
      `📎 Attaching the visual now — ${clipboardCopied ? "just paste (Ctrl+V) in the chat" : "attach the PNG that just downloaded"}.`;

    // 5) Open wa.me with cleaned phone + text
    const phone = (affiliate.phone || "").replace(/[^\d]/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");

    if (clipboardCopied) {
      toast.success("PNG copied — paste in WhatsApp with Ctrl+V");
    } else if (!phone) {
      toast("WhatsApp opened. No phone on file — pick a contact.", { duration: 6000 });
    } else {
      toast("PNG downloaded. Attach it in the WhatsApp chat that just opened.", { duration: 6000 });
    }
    setBusy(false);
  };

  const gen = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const tierAccent = TIER_COLORS[affiliate.tier] || TIER_COLORS.Base;

  // Target thresholds
  const paNewTarget = 8;
  const paRevTarget = 75_000;
  const legendNewTarget = 15;
  const legendRevTarget = 150_000;
  const newTarget = nextTier?.target === "Legend" ? legendNewTarget : nextTier?.target === "Pro Advanced" ? paNewTarget : 4;
  const revTarget = nextTier?.target === "Legend" ? legendRevTarget : paRevTarget;
  const newPct = Math.min(100, (cw.new_customers / newTarget) * 100);
  const revPct = Math.min(100, (cw.revenue / revTarget) * 100);
  const nextSlab = cm.next_slab;
  const slabPct = nextSlab ? Math.min(100, (cm.sales / nextSlab.sales_needed) * 100) : 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="snapshot-dialog"
        className="max-w-[720px] rounded-3xl p-0 gap-0 overflow-hidden bg-neutral-100 max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader className="px-6 pt-5 pb-4 flex-row items-center justify-between border-b border-border/40 bg-card">
          <DialogTitle className="text-sm font-semibold flex items-center gap-2 m-0">
            <Camera className="h-4 w-4 text-primary" />
            Snapshot for Sharing
          </DialogTitle>
          <div className="flex gap-2">
            <Button
              data-testid="snapshot-download-png"
              disabled={busy}
              size="sm"
              variant="outline"
              onClick={() => runExport("png")}
              className="rounded-full h-9"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> PNG
            </Button>
            <Button
              data-testid="snapshot-download-pdf"
              disabled={busy}
              size="sm"
              variant="outline"
              onClick={() => runExport("pdf")}
              className="rounded-full h-9"
            >
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button
              data-testid="snapshot-share-whatsapp"
              disabled={busy}
              size="sm"
              onClick={shareOnWhatsApp}
              className="rounded-full h-9 bg-[#25D366] hover:bg-[#1ebe5a] text-white"
              title={affiliate.phone ? `Send to ${affiliate.phone} on WhatsApp` : "Open WhatsApp (no phone on file)"}
            >
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
              WhatsApp
            </Button>
          </div>
        </DialogHeader>

        {/* Card frame */}
        <div className="p-5">
          <div
            ref={cardRef}
            data-testid="snapshot-card"
            style={{
              width: "640px",
              margin: "0 auto",
              background: "#ffffff",
              color: "#111827",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
              padding: "40px",
              borderRadius: "24px",
              boxSizing: "border-box",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            {/* Header row */}
            <Row justify="space-between" align="center" style={{ marginBottom: "28px" }}>
              <div>
                <Label style={{ color: "#F26A1E", letterSpacing: "0.2em" }}>FUELLED AFFILIATE PROGRAM</Label>
                <Label style={{ color: "#9CA3AF", marginTop: "6px" }}>
                  Performance Snapshot · {monthLabel}
                </Label>
              </div>
              <BrandMark />
            </Row>

            {/* Name + Tier */}
            <div style={{ marginBottom: "28px" }}>
              <Label style={{ color: "#9CA3AF", marginBottom: "4px" }}>HI</Label>
              <div style={{ fontSize: "34px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: "1.1" }}>
                {affiliate.name}
              </div>
              <Row align="center" style={{ marginTop: "12px", gap: "10px" }}>
                <Label style={{ color: "#6B7280" }}>CURRENT TIER</Label>
                <TierPill tier={affiliate.tier} accent={tierAccent} />
              </Row>
            </div>

            {/* Stats: <Month> Sales / New Customers / Commission */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px" }}>
              <StatBox label={`${monthLabel} Sales`} value={INR(cm.sales || 0)} />
              <StatBox
                label="New Customers"
                value={String(comm.new_customers_this_month || 0)}
                testId="snapshot-new-customers"
              />
              <StatBox label="Commission" value={`${comm.current_percent}%`} accent />
            </div>

            {/* Free products slab */}
            <Card style={{ marginBottom: "16px" }}>
              <Row align="center" style={{ gap: "8px", marginBottom: "10px" }}>
                <TrophyIcon />
                <Label style={{ color: "#374151", letterSpacing: "0.14em" }}>
                  FREE PRODUCTS UNLOCKED THIS MONTH
                </Label>
              </Row>
              <Row align="baseline" style={{ gap: "8px" }}>
                <div style={{ fontSize: "26px", fontWeight: 700, color: "#F26A1E", letterSpacing: "-0.02em" }}>
                  ₹{(cm.reward_slab_unlocked_mrp || 0).toLocaleString("en-IN")}
                </div>
                <div style={{ fontSize: "12px", color: "#9CA3AF", fontWeight: 500 }}>MRP</div>
              </Row>
              <ProgressBar pct={slabPct} style={{ marginTop: "12px" }} />
              {nextSlab ? (
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#4B5563", lineHeight: "1.5" }}>
                  <b style={{ color: "#111827" }}>₹{Math.round(nextSlab.gap).toLocaleString("en-IN")}</b> more sales
                  to unlock <b style={{ color: "#F26A1E" }}>₹{nextSlab.reward_mrp.toLocaleString("en-IN")}</b> slab
                </div>
              ) : (
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#059669", fontWeight: 600 }}>
                  Top slab unlocked this month
                </div>
              )}
            </Card>

            {/* Path to next tier */}
            {nextTier && (
              <Card style={{ background: "#F9FAFB", marginBottom: "16px" }}>
                <Row align="center" justify="space-between" style={{ marginBottom: "10px" }}>
                  <Row align="center" style={{ gap: "8px" }}>
                    <TargetIcon />
                    <Label style={{ color: "#374151", letterSpacing: "0.14em" }}>
                      PATH TO {nextTier.target.toUpperCase()}
                    </Label>
                  </Row>
                  {nextTier.unlock_reward_mrp > 0 && (
                    <UnlockPill amount={nextTier.unlock_reward_mrp} />
                  )}
                </Row>
                <div style={{ fontSize: "12px", color: "#4B5563", marginBottom: "14px", lineHeight: "1.5" }}>
                  {nextTier.requirement}
                </div>
                <ProgressRow
                  label="New Customers"
                  currentText={String(Math.round(cw.new_customers))}
                  targetText={String(newTarget)}
                  pct={newPct}
                />
                {nextTier.target !== "Pro" && (
                  <ProgressRow
                    label="Revenue in window"
                    currentText={INR(Math.round(cw.revenue))}
                    targetText={INR(revTarget)}
                    pct={revPct}
                    style={{ marginTop: "12px" }}
                  />
                )}
                <div style={{ marginTop: "12px", fontSize: "11px", color: "#9CA3AF" }}>
                  Trailing window: {cw.label}
                </div>
              </Card>
            )}

            {/* Fast-track */}
            {ft && ft.status === "pending" && ft.days_remaining > 0 && (
              <Card style={{ border: "2px solid rgba(242,106,30,0.28)", background: "rgba(242,106,30,0.06)", marginBottom: "16px" }}>
                <Row align="center" style={{ gap: "8px", marginBottom: "8px" }}>
                  <ZapIcon color="#F26A1E" />
                  <Label style={{ color: "#F26A1E", letterSpacing: "0.14em" }}>
                    FAST TRACK · {ft.days_remaining} DAYS LEFT
                  </Label>
                </Row>
                <div style={{ fontSize: "13px", color: "#1F2937", lineHeight: "1.5" }}>
                  Bring{" "}
                  <b>
                    {Math.max(0, ft.target_new_customers - ft.new_customers_so_far)} more new customer
                    {ft.target_new_customers - ft.new_customers_so_far !== 1 ? "s" : ""}
                  </b>{" "}
                  by{" "}
                  <b>{new Date(ft.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</b>{" "}
                  to unlock <b style={{ color: "#F26A1E" }}>₹{ft.reward_mrp.toLocaleString("en-IN")} MRP</b>
                </div>
                <ProgressBar
                  pct={Math.min(100, (ft.new_customers_so_far / ft.target_new_customers) * 100)}
                  style={{ marginTop: "10px", background: "#ffffff" }}
                />
                <div style={{ marginTop: "6px", fontSize: "11px", color: "#4B5563" }}>
                  {ft.new_customers_so_far} of {ft.target_new_customers} so far
                </div>
              </Card>
            )}

            {/* Commission slabs */}
            <Card style={{ background: "#F9FAFB", marginBottom: "12px" }}>
              <Row align="center" style={{ gap: "8px", marginBottom: "12px" }}>
                <TrendUpIcon />
                <Label style={{ color: "#374151", letterSpacing: "0.14em" }}>
                  {affiliate.tier.toUpperCase()} COMMISSION SLABS
                </Label>
              </Row>
              <CommissionTable tier={affiliate.tier} activeNew={comm.new_customers_this_month || 0} />
              {comm.next_step && (
                <div style={{ marginTop: "12px", fontSize: "12px", color: "#374151", lineHeight: "1.5" }}>
                  <b>{comm.next_step.new_customers_needed}</b> more new customer
                  {comm.next_step.new_customers_needed !== 1 ? "s" : ""} this month → commission percent jumps to{" "}
                  <b style={{ color: "#F26A1E" }}>{comm.next_step.next_percent}%</b>
                </div>
              )}
            </Card>

            {/* Footer */}
            <Row justify="space-between" align="center" style={{ paddingTop: "16px", borderTop: "1px solid #E5E7EB" }}>
              <Label style={{ color: "#9CA3AF" }}>GENERATED {gen.toUpperCase()}</Label>
              <Label style={{ color: "#F26A1E", fontWeight: 700 }}>FUELLED.IN</Label>
            </Row>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Sub-components (all pixel-explicit) -------------------- */

function Row({ children, align = "center", justify = "flex-start", style = {} }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: align,
        justifyContent: justify,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children, style = {} }) {
  return (
    <div
      style={{
        fontSize: "10px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        color: "#6B7280",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        padding: "16px",
        background: "#ffffff",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function StatBox({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        borderRadius: "16px",
        padding: "14px",
        background: accent ? "#F26A1E" : "#F9FAFB",
        border: accent ? "1px solid #F26A1E" : "1px solid #E5E7EB",
        color: accent ? "#ffffff" : "#111827",
      }}
    >
      <div
        style={{
          fontSize: "9px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          color: accent ? "rgba(255,255,255,0.85)" : "#6B7280",
          marginBottom: "6px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "18px", fontWeight: 700, letterSpacing: "-0.01em" }}>{value}</div>
    </div>
  );
}

function ProgressBar({ pct, style = {} }) {
  const width = `${Math.max(0, Math.min(100, pct))}%`;
  return (
    <div
      style={{
        width: "100%",
        height: "6px",
        background: "#E5E7EB",
        borderRadius: "999px",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        style={{
          width,
          height: "6px",
          background: "#F26A1E",
          borderRadius: "999px",
        }}
      />
    </div>
  );
}

function ProgressRow({ label, currentText, targetText, pct, style = {} }) {
  return (
    <div style={style}>
      <Row justify="space-between" align="center" style={{ marginBottom: "6px" }}>
        <div style={{ fontSize: "12px", color: "#6B7280" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "#111827", fontWeight: 600 }}>
          {currentText}{" "}
          <span style={{ color: "#9CA3AF", fontWeight: 400 }}>/ {targetText}</span>
        </div>
      </Row>
      <ProgressBar pct={pct} />
    </div>
  );
}

const TIER_COLORS = {
  Base: { bg: "#F3F4F6", fg: "#374151" },
  Pro: { bg: "#DBEAFE", fg: "#1D4ED8" },
  "Pro Advanced": { bg: "#F3E8FF", fg: "#7E22CE" },
  Legend: { bg: "#FEF3C7", fg: "#B45309" },
};

function TierPill({ tier, accent }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "999px",
        background: accent.bg,
        color: accent.fg,
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {tier}
    </span>
  );
}

function UnlockPill({ amount }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        borderRadius: "999px",
        background: "rgba(242,106,30,0.1)",
        color: "#F26A1E",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      <ZapIcon size={11} color="#F26A1E" /> ₹{amount.toLocaleString("en-IN")} MRP UNLOCK
    </span>
  );
}

const COMMISSION_SLABS = {
  Base: [
    { range: "0–3 new", pct: 10 },
    { range: "4–10 new", pct: 12 },
    { range: "11+ new", pct: 15 },
  ],
  Pro: [
    { range: "0–3 new", pct: 10 },
    { range: "4–10 new", pct: 12 },
    { range: "11+ new", pct: 15 },
  ],
  "Pro Advanced": [
    { range: "0–3 new", pct: 10 },
    { range: "4–10 new", pct: 12 },
    { range: "11+ new", pct: 15 },
  ],
  Legend: [
    { range: "0–3 new", pct: 12 },
    { range: "4–10 new", pct: 15 },
    { range: "11+ new", pct: 18 },
  ],
};

function CommissionTable({ tier, activeNew }) {
  const slabs = COMMISSION_SLABS[tier] || COMMISSION_SLABS.Base;
  const activeIdx = activeNew <= 3 ? 0 : activeNew <= 10 ? 1 : 2;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
      {slabs.map((s, i) => {
        const isActive = i === activeIdx;
        return (
          <div
            key={i}
            style={{
              padding: "12px 8px",
              textAlign: "center",
              borderRadius: "12px",
              background: isActive ? "#F26A1E" : "#ffffff",
              border: isActive ? "1px solid #F26A1E" : "1px solid #E5E7EB",
              color: isActive ? "#ffffff" : "#111827",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: isActive ? "rgba(255,255,255,0.85)" : "#9CA3AF",
                marginBottom: "4px",
              }}
            >
              {s.range}
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, letterSpacing: "-0.01em" }}>
              {s.pct}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------- Inline SVG icons (html2canvas-safe) -------------------- */

function BrandMark() {
  return (
    <div
      style={{
        width: "36px",
        height: "36px",
        borderRadius: "10px",
        background: "#F26A1E",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ZapIcon color="#ffffff" size={16} filled />
    </div>
  );
}

function ZapIcon({ size = 14, color = "#F26A1E", filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F26A1E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F26A1E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function TrendUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F26A1E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}
